import React from "react";
import { ArrowUpRight, Sparkles, TrendingUp } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-36 pb-28 md:pt-44 md:pb-40 overflow-hidden"
    >
      <div className="absolute inset-0 mk-grid-bg pointer-events-none" aria-hidden />
      <div
        className="mk-orb"
        style={{ width: 560, height: 560, background: "#0A66FF", top: -140, left: -140 }}
        aria-hidden
      />
      <div
        className="mk-orb"
        style={{ width: 380, height: 380, background: "#00D4FF", top: 60, right: -100, animationDelay: "3s" }}
        aria-hidden
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="mk-fade-up flex justify-start">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 backdrop-blur-md">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#00D4FF] opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00D4FF]" />
            </span>
            <span className="text-[11px] tracking-[0.18em] font-medium text-slate-300 uppercase">
              UK Technology Studio · Trading since 2019
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 mt-10 lg:mt-14 items-center">
          <div className="lg:col-span-7">
            <h1
              className="mk-fade-up font-display text-[46px] sm:text-6xl lg:text-7xl xl:text-[86px] leading-[1.02] tracking-tight text-white"
              data-testid="hero-headline"
              style={{ animationDelay: "0.1s" }}
            >
              Digital products,{" "}
              <span className="bg-gradient-to-r from-[#00D4FF] via-[#3B8BFF] to-[#0A66FF] bg-clip-text text-transparent">
                platforms and systems
              </span>{" "}
              built to move businesses forward.
            </h1>

            <p
              className="mk-fade-up mt-8 max-w-2xl text-lg md:text-xl text-slate-400 leading-relaxed"
              style={{ animationDelay: "0.25s" }}
            >
              Mackbe Tech Ltd is a UK-based technology studio helping companies
              design, build and launch high-quality websites, mobile applications,
              internal systems and AI-powered digital products.
            </p>

            <div
              className="mk-fade-up mt-10 flex flex-col sm:flex-row gap-3"
              style={{ animationDelay: "0.4s" }}
            >
              <a
                href="#contact"
                data-testid="hero-start-project-button"
                className="inline-flex items-center justify-center gap-2 mk-btn-primary px-7 py-4 rounded-full text-[15px] font-semibold"
              >
                Start a Project <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="#services"
                data-testid="hero-view-capabilities-button"
                className="inline-flex items-center justify-center gap-2 mk-btn-secondary px-7 py-4 rounded-full text-[15px] font-semibold"
              >
                View Our Capabilities
              </a>
            </div>

            <p
              className="mk-fade-up mt-5 text-[13px] text-slate-500 max-w-xl"
              style={{ animationDelay: "0.5s" }}
              data-testid="hero-investment-line"
            >
              Typical projects range from high-quality business websites to fully
              custom platforms.
            </p>

            <p
              className="mk-fade-up mt-12 text-[11px] uppercase tracking-[0.22em] text-slate-500"
              style={{ animationDelay: "0.6s" }}
              data-testid="hero-trust-line"
            >
              Trading since October 2019 · Supporting SMEs, startups & professional services
            </p>
          </div>

          <div
            className="lg:col-span-5 mk-fade-up"
            style={{ animationDelay: "0.35s" }}
            aria-hidden
          >
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative w-full max-w-[540px] mx-auto h-[520px]">
      {/* Background glow */}
      <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-[#0A66FF]/25 via-transparent to-[#00D4FF]/15 blur-3xl" />

      {/* Enterprise dashboard mock */}
      <div className="absolute top-0 left-0 right-4 mk-codepanel rotate-[-2.5deg] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
        <div className="mk-bar flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </div>
          <span className="text-[11px] font-mono text-slate-500">
            Operations · Live
          </span>
        </div>
        <div className="px-5 py-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                Monthly recurring
              </div>
              <div className="mt-1 font-display text-2xl text-white tracking-tight">
                £48,200
              </div>
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/30 px-2.5 py-1 text-[11px] text-[#7DD3FC]">
              <TrendingUp className="h-3 w-3" />
              +12.4%
            </div>
          </div>
          {/* Bar chart */}
          <div className="flex items-end gap-2 h-20">
            {[42, 58, 36, 70, 48, 82, 64, 92, 76, 88, 96, 84].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-gradient-to-t from-[#0A66FF]/60 to-[#00D4FF]/80"
                style={{ height: `${h}%`, opacity: 0.4 + (i / 12) * 0.6 }}
              />
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between text-[10px] font-mono text-slate-600">
            <span>JAN</span><span>APR</span><span>JUL</span><span>OCT</span><span>DEC</span>
          </div>
        </div>
      </div>

      {/* Floating product card 1 — booking */}
      <div className="absolute right-0 top-[260px] w-[230px] mk-card mk-glow rounded-2xl p-5 rotate-[4deg]">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400">
            Web App
          </div>
          <div className="h-2 w-2 rounded-full bg-[#00D4FF] mk-pulse" />
        </div>
        <div className="mt-3 font-display text-lg text-white leading-tight">
          Booking platform
        </div>
        <div className="mt-1 text-xs text-slate-400">+38% conversions</div>
        <div className="mt-4 h-16 rounded-lg bg-gradient-to-br from-[#0A66FF]/30 to-[#00D4FF]/10 border border-white/5" />
      </div>

      {/* AI assist card */}
      <div className="absolute left-2 bottom-2 w-[260px] mk-card rounded-2xl p-5 rotate-[-2deg]">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#00D4FF]" />
          <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">
            AI Assist
          </span>
        </div>
        <div className="mt-3 space-y-2">
          <div className="h-2 w-full rounded-full bg-white/8 overflow-hidden">
            <div className="h-full w-[78%] bg-gradient-to-r from-[#0A66FF] to-[#00D4FF]" />
          </div>
          <div className="h-2 w-3/5 rounded-full bg-white/6" />
          <div className="h-2 w-4/5 rounded-full bg-white/6" />
        </div>
        <div className="mt-4 text-xs text-slate-300">
          Production ready in <span className="text-white font-semibold">14 days</span>
        </div>
      </div>

      <div className="absolute right-10 bottom-12 inline-flex items-center gap-1.5 rounded-full bg-[#0A66FF]/15 border border-[#0A66FF]/30 px-3 py-1 text-[11px] font-medium text-[#7DB7FF] backdrop-blur">
        <span className="h-1.5 w-1.5 rounded-full bg-[#00D4FF]" />
        Live
      </div>
    </div>
  );
}
